import React, { useEffect, useState, forwardRef } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Slide,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Button,
  TextField,
  Grid,
  Typography,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import validationRules, {
  maxLengthValidation,
} from '../../utils/validations.util';
import Iconify from '../common/iconify.component';
import Loading from '../table-elements/loading.component';
import SnackbarInfo from '../common/snackbar-info.component';
import {
  viewDataById,
  createNewData,
  updateDataById,
} from '../../services/series.service';
import StyledDialogActions from '../../theme/dialogActionStyles';
import Status from '../form-elements/status.component';
import { CDN_BASE_URL } from '../../config/const.config';
import Upload from '../common/upload.component';

const Transition = forwardRef((props, ref) => (
  <Slide direction="up" ref={ref} {...props} />
));

const AddEditSeries = ({ title, dataId, onCancel, onSuccess }) => {
  const {
    control,
    formState: { errors },
    handleSubmit,
    setValue,
  } = useForm({
    defaultValues: {
      title: '',
      year: null,
      description: null,
      landscapePoster: null,
      portraitPoster: null,
      imdbId: null,
      status: 1,
    },
  });
  const [loading, setLoading] = useState(true);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [seriesDetails, setSeriesDetails] = useState({
    title: '',
    year: '',
  });

  const [uploadedImg, setUploadedImg] = useState({
    landscapePoster: '',
    portraitPoster: '',
  });
  const [snackbarInfo, setSnackbarInfo] = useState({
    show: false,
    type: '',
    message: '',
  });

  const saveNewData = (payload) => {
    createNewData(payload)
      .then(() => {
        onSuccess('New Series created successfully.');
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        setFormSubmitted(false);
      });
  };

  const updateExistingData = (payload) => {
    updateDataById(dataId, payload)
      .then(() => {
        onSuccess('Series details updated successfully.');
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        setFormSubmitted(false);
      });
  };

  const onFormSubmit = (data) => {
    setFormSubmitted(true);
    setSnackbarInfo({
      ...snackbarInfo,
      show: false,
    });

    const payload = {
      title: data.title,
      year: data.year,
      description: data.description,
      landscapePoster: data.landscapePoster,
      portraitPoster: data.portraitPoster,
      imdbId: data.imdbId,
      status: data.status,
    };

    if (dataId) {
      updateExistingData(payload);
    } else {
      saveNewData(payload);
    }
  };

  useEffect(() => {
    if (!dataId) {
      setLoading(false);
      return;
    }

    viewDataById(dataId)
      .then((res) => {
        // Fill form values
        setSeriesDetails({
          title: res.data?.title || '',
          year: res.data?.year || '',
        });
        setValue('title', res.data?.title || '');
        setValue('year', res.data?.year || null);
        setValue('description', res.data?.description || null);
        setValue('landscapePoster', res.data?.landscapePoster || null);
        setValue('portraitPoster', res.data?.portraitPoster || null);
        setValue('imdbId', res.data?.imdbId || null);
        setValue('status', res.data?.status || 0);

        setLoading(false);
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        setLoading(false);
      });
  }, [dataId]);

  return (
    <Dialog
      open
      aria-labelledby="add-dialog-title"
      aria-describedby="add-dialog-description"
      TransitionComponent={Transition}
      fullWidth
      maxWidth="md"
    >
      <DialogTitle id="add-dialog-title">{title}</DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onCancel}
        size="small"
        sx={{
          position: 'absolute',
          right: 10,
          top: 15,
          color: (theme) => theme.palette.grey[500],
        }}
      >
        <Iconify icon="ic:outline-close" />
      </IconButton>

      <DialogContent dividers id="add-dialog-description">
        {loading && <Loading />}

        {!loading && (
          <form id="add-edit-form" onSubmit={handleSubmit(onFormSubmit)}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={12} md={12}>
                <Controller
                  name="title"
                  control={control}
                  rules={{
                    ...validationRules.REQUIRED,
                    ...maxLengthValidation(128),
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      onChange={(e) => {
                        field.onChange(e);
                        const newValue = e.target.value;
                        seriesDetails.title = newValue;
                        setSeriesDetails({
                          ...seriesDetails,
                          title: newValue,
                        });
                      }}
                      label="Title *"
                      type="text"
                      error={!!errors.title}
                      helperText={errors?.title?.message || null}
                      fullWidth
                      variant="filled"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={6}>
                <Controller
                  name="year"
                  control={control}
                  rules={{
                    ...validationRules.REQUIRED,
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      onChange={(e) => {
                        field.onChange(e);
                        const newValue = e.target.value;
                        setSeriesDetails({
                          ...seriesDetails,
                          year: newValue,
                        });
                      }}
                      label="Year *"
                      type="number"
                      error={!!errors.year}
                      helperText={errors?.year?.message || null}
                      fullWidth
                      variant="filled"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={6}>
                <Controller
                  name="imdbId"
                  control={control}
                  rules={{
                    ...maxLengthValidation(16),
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="IMDB ID "
                      type="text"
                      error={!!errors.imdbId}
                      helperText={errors?.imdbId?.message || null}
                      fullWidth
                      variant="filled"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={12}>
                <Controller
                  name="description"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Description "
                      type="text"
                      multiline
                      minRows={5}
                      maxRows={5}
                      error={!!errors.description}
                      helperText={errors?.description?.message || null}
                      fullWidth
                      variant="filled"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={8} sm={8} md={8}>
                <Controller
                  name="landscapePoster"
                  control={control}
                  rules={{
                    ...validationRules.OPTIONAL_URL,
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Landscape Poster (16x9) "
                      type="text"
                      error={!!errors.landscapePoster}
                      helperText={errors?.landscapePoster?.message || null}
                      fullWidth
                      variant="filled"
                    />
                  )}
                />
                <Typography variant="body2" color="secondary" mt={1}>
                  {CDN_BASE_URL}
                </Typography>
              </Grid>
              <Grid item xs={4} sm={4} md={4}>
                <Box mt={1}>
                  <Upload
                    title="Upload Landscape Poster (16x9)"
                    updateImage={(v) => {
                      setValue('landscapePoster', v);
                      setUploadedImg({
                        ...uploadedImg,
                        landscapePoster: v,
                      });
                    }}
                    s3Folder="posters"
                    sourceType="poster16x9FileName"
                    aspectRatio="16.9"
                    allowedSize={null}
                    setSnackbarInfo={setSnackbarInfo}
                    type="poster"
                    isSeries
                    details={seriesDetails}
                  />
                </Box>
              </Grid>
              <Grid item xs={8} sm={8} md={8}>
                {uploadedImg.landscapePoster && (
                  <img
                    src={uploadedImg.landscapePoster}
                    style={{
                      maxHeight: '150px',
                      maxWidth: '150px',
                      objectFit: 'contain',
                    }}
                    alt="Uploaded"
                  />
                )}
              </Grid>
              <Grid item xs={8} sm={8} md={8}>
                <Controller
                  name="portraitPoster"
                  control={control}
                  rules={{
                    ...validationRules.OPTIONAL_URL,
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Portrait Poster (3x4) "
                      type="text"
                      error={!!errors.portraitPoster}
                      helperText={errors?.portraitPoster?.message || null}
                      fullWidth
                      variant="filled"
                    />
                  )}
                />
                <Typography variant="body2" color="secondary" mt={1}>
                  {CDN_BASE_URL}
                </Typography>
              </Grid>
              <Grid item xs={4} sm={4} md={4}>
                <Box mt={1}>
                  <Upload
                    title="Upload Portrait Poster (3x4)"
                    updateImage={(v) => {
                      setValue('portraitPoster', v);
                      setUploadedImg({
                        ...uploadedImg,
                        portraitPoster: v,
                      });
                    }}
                    s3Folder="posters"
                    sourceType="poster3x4FileName"
                    aspectRatio="16.9"
                    allowedSize={null}
                    setSnackbarInfo={setSnackbarInfo}
                    type="poster"
                  />
                </Box>
              </Grid>
              <Grid item xs={8} sm={8} md={8}>
                {uploadedImg.portraitPoster && (
                  <img
                    src={uploadedImg.portraitPoster}
                    style={{
                      maxHeight: '150px',
                      maxWidth: '150px',
                      objectFit: 'contain',
                    }}
                    alt="Uploaded"
                  />
                )}
              </Grid>
              <Grid item xs={12} sm={6} md={6}>
                <Controller
                  name="status"
                  control={control}
                  rules={validationRules.REQUIRED}
                  render={({ field: { onChange, value } }) => (
                    <Status
                      id="series_status"
                      name="series_status"
                      label="Status"
                      defaultValue={value}
                      onChange={onChange}
                      validOptions={[0, 1]}
                      sx={{ width: '100%' }}
                      error={errors?.status?.message || ''}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </form>
        )}
      </DialogContent>

      <StyledDialogActions>
        <Grid>
          <Button
            type="submit"
            form="add-edit-form"
            color="primary"
            variant="contained"
            disabled={loading || formSubmitted}
            sx={{ marginRight: 2 }}
          >
            Save
          </Button>

          <Button
            color="secondary"
            variant="contained"
            onClick={onCancel}
            disabled={formSubmitted}
          >
            Cancel
          </Button>
        </Grid>
        <Grid>
          <Typography variant="body2" color="secondary">
            * indicates a required field
          </Typography>
        </Grid>
      </StyledDialogActions>

      {snackbarInfo.show && (
        <SnackbarInfo type={snackbarInfo.type} message={snackbarInfo.message} />
      )}
    </Dialog>
  );
};

AddEditSeries.propTypes = {
  title: PropTypes.string.isRequired,
  dataId: PropTypes.number.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
};

export default AddEditSeries;
